/**
 * Unique poster share links for feed "Match a friend": /poster/{posterId}?ref={referrerUid}
 * Captured in sessionStorage on poster page load; applied to new user docs on signup.
 * Signed-in users who open the same link get matchFriendReferralFromFeed merged too (matcha reward path).
 */

import { doc, setDoc, serverTimestamp } from 'firebase/firestore';

const STORAGE_KEY = 'luvPosterReferral';
const ATTRIBUTION_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

export function isLikelyFirebaseUid(s) {
  if (typeof s !== 'string') return false;
  return /^[A-Za-z0-9_-]{20,128}$/.test(s);
}

export function buildPosterShareUrl(origin, posterId, referrerUid) {
  const o = (origin || '').replace(/\/$/, '');
  const base = `${o}/poster/${posterId}`;
  if (referrerUid && isLikelyFirebaseUid(referrerUid)) {
    return `${base}?ref=${encodeURIComponent(referrerUid)}`;
  }
  return base;
}

/**
 * Call when /poster/:id loads. Persists ref= from query, or clears stale entry if same poster opened without ref.
 */
export function capturePosterReferralFromSearch(posterId) {
  if (typeof window === 'undefined' || !posterId) return;

  const ref = new URLSearchParams(window.location.search).get('ref');

  if (!ref) {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const o = JSON.parse(raw);
      if (o && o.posterId === posterId) sessionStorage.removeItem(STORAGE_KEY);
    } catch (_) {}
    return;
  }

  const decoded = decodeURIComponent(ref.trim());
  if (!isLikelyFirebaseUid(decoded) || decoded === posterId) return;

  try {
    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        referrerUid: decoded,
        posterId,
        ts: Date.now(),
      }),
    );
  } catch (_) {}
}

/**
 * Returns pending referral for signup if fresh enough; does not clear (caller clears after successful user write).
 */
export function getPendingPosterReferralForSignup() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const o = JSON.parse(raw);
    if (!o || typeof o.referrerUid !== 'string' || typeof o.posterId !== 'string' || typeof o.ts !== 'number') {
      return null;
    }
    if (!isLikelyFirebaseUid(o.referrerUid)) return null;
    if (Date.now() - o.ts > ATTRIBUTION_MAX_AGE_MS) {
      sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return { referrerUid: o.referrerUid, posterId: o.posterId, ts: o.ts };
  } catch {
    return null;
  }
}

export function clearPosterReferralFromSession() {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch (_) {}
}

/**
 * If the URL has ?ref= for this poster, merge matchFriendReferralFromFeed onto the signed-in user
 * (same shape as SignUpPopup) so Cloud Functions can credit the referrer after mutual unmask with the poster owner.
 */
export async function mergePosterReferralIntoUserDocIfPresent(db, userId, posterId) {
  if (!db || !userId || !posterId || typeof window === 'undefined') return;

  const rawRef = new URLSearchParams(window.location.search).get('ref');
  if (!rawRef) return;

  const referrerUid = decodeURIComponent(rawRef.trim());
  if (!isLikelyFirebaseUid(referrerUid) || referrerUid === posterId || referrerUid === userId) return;

  try {
    await setDoc(
      doc(db, 'users', userId),
      {
        matchFriendReferralFromFeed: {
          referrerUid,
          posterId,
          capturedAt: serverTimestamp(),
        },
      },
      { merge: true },
    );
  } catch (err) {
    console.warn('mergePosterReferralIntoUserDocIfPresent:', err);
  }
}
